import { ILocation, LocationType } from "./Location";
import {
  LocationDto,
  EmsPortalDto,
  SkidDeviceDto,
  FreestandingBatteryDto,
  TwinChargerDto,
  FveDto,
} from "./LocationDTO";
import { Skid } from "./Skid";
import { EmsPortal } from "./EmsPortal";
import { FreestandingBattery } from "./FreestandingBattery";
import { TwinCharger } from "./TwinCharger";
import { Fve } from "./Fve";

/**
 * Creates the correct {@link ILocation} subclass for a given type discriminator or DTO.
 *
 * This is the single entry point for turning raw API JSON into typed domain objects:
 * ```ts
 * const location = LocationFactory.FromDto(LocationDto.fromJson(apiResponse));
 * ```
 */
export class LocationFactory {
  /** Allocates an empty instance of the subclass that matches `type`. */
  static create(type: LocationType): ILocation {
    switch (type) {
      case LocationType.Skid:
        return new Skid();
      case LocationType.EmsPortal:
        return new EmsPortal();
      case LocationType.FreestandingBattery:
        return new FreestandingBattery();
      case LocationType.TwinCharger:
        return new TwinCharger();
      case LocationType.Fve:
        return new Fve();
      default:
        throw new Error("Invalid location type");
    }
  }

  /** Reads `dto.type`, picks the right subclass, and calls its `FromDto`. */
  static FromDto(dto: LocationDto): ILocation {
    const location = LocationFactory.create(
      dto.type as unknown as LocationType,
    );
    return location.FromDto(dto);
  }

  static CreateDto(type: LocationType): LocationDto {
    const dto = LocationDto.WithDefaults();
    dto.type = type;
    switch (type) {
      case LocationType.Skid:
        dto.skid = SkidDeviceDto.WithDefaults();
        break;
      case LocationType.EmsPortal:
        dto.emsPortal = EmsPortalDto.WithDefaults();
        break;
      case LocationType.FreestandingBattery:
        dto.freestandingBattery = FreestandingBatteryDto.WithDefaults();
        break;
      case LocationType.TwinCharger:
        dto.twinCharger = TwinChargerDto.WithDefaults();
        break;
      case LocationType.Fve:
        dto.fve = FveDto.WithDefaults();
        break;
    }
    return dto;
  }
}
